import * as React from "react";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useLanguage } from "contexts/language.context";

function NcrPhotoGallery({ photos = [], reference }) {
  const { label } = useLanguage();
  const [active, setActive] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  const titleId = React.useId();
  const photoLabel = label("ncr-photo") || "Photo";
  const photosLabel = label("ncr-photos") || "photos";
  const select = (index) => {
    setActive(index);
    setFailed(false);
  };
  const move = (delta) =>
    select((active + delta + photos.length) % photos.length);
  if (!photos.length) return null;
  const current = active === null ? null : photos[active];
  return (
    <>
      <Box
        sx={{ display: "grid", gridTemplateColumns: "repeat(2, 80px)", gap: 1 }}
      >
        {photos.slice(0, 4).map((photo, index) => (
          <ButtonBase
            key={index}
            type="button"
            aria-label={`${label("ncr-view-photos") || "View photos"} · ${reference} · ${index + 1} / ${photos.length}`}
            onClick={() => select(index)}
            sx={{
              width: 80,
              height: 80,
              position: "relative",
              overflow: "hidden",
              borderRadius: 1,
              bgcolor: "action.hover",
              "&.Mui-focusVisible": {
                outline: "2px solid",
                outlineColor: "info.main",
                outlineOffset: 2,
              },
            }}
          >
            <Box
              component="img"
              src={photo.url}
              alt={`${photoLabel} ${index + 1}`}
              loading="lazy"
              decoding="async"
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            {index === 3 && photos.length > 4 ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "rgba(0, 0, 0, 0.65)",
                  color: "common.white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 0.5,
                }}
              >
                <Typography variant="caption" color="inherit" fontWeight="bold">
                  + {photos.length - 4} {photosLabel}
                </Typography>
              </Box>
            ) : null}
          </ButtonBase>
        ))}
      </Box>
      {current ? (
        <Dialog
          open
          fullWidth
          maxWidth="md"
          aria-labelledby={titleId}
          onClose={() => setActive(null)}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
              event.preventDefault();
              move(event.key === "ArrowLeft" ? -1 : 1);
            }
          }}
        >
          <DialogTitle
            id={`${titleId}-header`}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              id={titleId}
              component="span"
              variant="h6"
              sx={{ minWidth: 0, overflowWrap: "anywhere" }}
            >
              {label("ncr-view-photos") || "View photos"} · {reference}
            </Typography>
            <IconButton
              aria-label={label("ncr-close-photos") || "Close photos"}
              onClick={() => setActive(null)}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                minHeight: { xs: 200, md: 300 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              {failed ? (
                <Box role="alert" sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="body2">
                    {label("ncr-photo-failed") || "Unable to load this photo."}
                  </Typography>
                  <Button
                    onClick={() => {
                      setFailed(false);
                      setRetry((count) => count + 1);
                    }}
                  >
                    {label("ncr-retry") || "Retry"}
                  </Button>
                </Box>
              ) : (
                <Box
                  component="img"
                  key={`${active}-${retry}`}
                  src={current.url}
                  alt={current.remarks || `${photoLabel} ${active + 1}`}
                  onError={() => setFailed(true)}
                  sx={{
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: "50vh",
                    objectFit: "contain",
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                my: 1,
              }}
            >
              <IconButton
                aria-label={label("ncr-previous-photo") || "Previous photo"}
                disabled={photos.length < 2}
                onClick={() => move(-1)}
              >
                <ChevronLeft />
              </IconButton>
              <Typography variant="body2" role="status">
                {active + 1} / {photos.length}
              </Typography>
              <IconButton
                aria-label={label("ncr-next-photo") || "Next photo"}
                disabled={photos.length < 2}
                onClick={() => move(1)}
              >
                <ChevronRight />
              </IconButton>
            </Box>
            {current.remarks ? (
              <Typography
                variant="body2"
                sx={{ mb: 1, overflowWrap: "anywhere" }}
              >
                {current.remarks}
              </Typography>
            ) : null}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(64px, 1fr))",
                gap: 1,
              }}
            >
              {photos.map((photo, index) => (
                <ButtonBase
                  key={index}
                  type="button"
                  aria-label={`${photoLabel} ${index + 1}`}
                  aria-pressed={active === index}
                  onClick={() => select(index)}
                  sx={{
                    height: 64,
                    overflow: "hidden",
                    borderRadius: 1,
                    border: "2px solid",
                    borderColor: active === index ? "info.main" : "transparent",
                    "&.Mui-focusVisible": {
                      outline: "2px solid",
                      outlineColor: "info.main",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={photo.url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </ButtonBase>
              ))}
            </Box>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

export default React.memo(NcrPhotoGallery);
